import { lazy } from "react";
import { Navigate, Route } from "react-router";

import { Gate } from "./gate";
import { ForgotPassword } from "./routes/ForgotPassword";
import { OAuthConsent } from "./routes/OAuthConsent";
import { OAuthSignIn } from "./routes/OAuthSignIn";
import { ResetPassword } from "./routes/ResetPassword";

/**
 * Every page is loaded on demand.
 *
 * These were static imports, which put the whole console — Billing, the nine
 * company panels, the DocuSeal health screen, the Operations monitors — into
 * one 682 kB chunk that an operator downloaded in full before the work queue
 * rendered a single row. Splitting them is the biggest first-load win the
 * console had available, and it costs nothing at runtime: the `<Suspense>`
 * around the `<Outlet />` in `gate.tsx` covers the moment a chunk is still
 * arriving, and the sidebar warms the common ones on hover.
 *
 * The `.then(m => ({ default: m.X }))` shape is here because these modules use
 * NAMED exports and `React.lazy` resolves a default. Two routes served by one
 * module (Documents/DocumentDetail, Fax/FaxDetail, …) share a single dynamic
 * import, so they stay one chunk between them.
 *
 * `Gate` stays static: the gate IS the shell every page renders into, so
 * lazy-loading it would only add a round trip in front of everything.
 */
const PartnersOverview = lazy(() =>
  import("./routes/partners/Overview").then((m) => ({
    default: m.PartnersOverview,
  })),
);
const PartnerApplications = lazy(() =>
  import("./routes/partners/Applications").then((m) => ({
    default: m.PartnerApplications,
  })),
);
const PartnerApplicationDetail = lazy(() =>
  import("./routes/partners/ApplicationDetail").then((m) => ({
    default: m.PartnerApplicationDetail,
  })),
);
const PartnersList = lazy(() =>
  import("./routes/partners/PartnersList").then((m) => ({
    default: m.PartnersList,
  })),
);
const PartnerDetail = lazy(() =>
  import("./routes/partners/PartnerDetail").then((m) => ({
    default: m.PartnerDetail,
  })),
);
const PartnerEarnings = lazy(() =>
  import("./routes/partners/Earnings").then((m) => ({
    default: m.PartnerEarnings,
  })),
);
const PartnerPayouts = lazy(() =>
  import("./routes/partners/Payouts").then((m) => ({
    default: m.PartnerPayouts,
  })),
);
const PartnerPayoutBatch = lazy(() =>
  import("./routes/partners/PayoutBatch").then((m) => ({
    default: m.PartnerPayoutBatch,
  })),
);
const PartnerFeesAndSettings = lazy(() =>
  import("./routes/partners/FeesAndSettings").then((m) => ({
    default: m.PartnerFeesAndSettings,
  })),
);
const Alerts = lazy(() =>
  import("./routes/Alerts").then((m) => ({ default: m.Alerts })),
);
const Approvals = lazy(() =>
  import("./routes/approvals/Approvals").then((m) => ({
    default: m.Approvals,
  })),
);
const ApprovalDetailRoute = lazy(() =>
  import("./routes/approvals/Approvals").then((m) => ({
    default: m.ApprovalDetailRoute,
  })),
);
const Accounting = lazy(() =>
  import("./routes/accounting/Accounting").then((m) => ({
    default: m.Accounting,
  })),
);
const ChartTemplates = lazy(() =>
  import("./routes/accounting/ChartTemplates").then((m) => ({
    default: m.ChartTemplates,
  })),
);
const BookWorkspace = lazy(() =>
  import("./routes/accounting/book/BookWorkspace").then((m) => ({
    default: m.BookWorkspace,
  })),
);
const Billing = lazy(() =>
  import("./routes/Billing").then((m) => ({ default: m.Billing })),
);
const InvoiceDetail = lazy(() =>
  import("./routes/billing/InvoiceDetail").then((m) => ({
    default: m.InvoiceDetail,
  })),
);
const PaymentIntentDetail = lazy(() =>
  import("./routes/billing/PaymentIntentDetail").then((m) => ({
    default: m.PaymentIntentDetail,
  })),
);
const DisputeDetail = lazy(() =>
  import("./routes/billing/DisputeDetail").then((m) => ({
    default: m.DisputeDetail,
  })),
);
const PayoutDetail = lazy(() =>
  import("./routes/billing/PayoutDetail").then((m) => ({
    default: m.PayoutDetail,
  })),
);
const SubscriptionDetail = lazy(() =>
  import("./routes/billing/SubscriptionDetail").then((m) => ({
    default: m.SubscriptionDetail,
  })),
);
const Companies = lazy(() =>
  import("./routes/Companies").then((m) => ({ default: m.Companies })),
);
const CompanyDetail = lazy(() =>
  import("./routes/company/CompanyDetail").then((m) => ({
    default: m.CompanyDetail,
  })),
);
const Dashboard = lazy(() =>
  import("./routes/Dashboard").then((m) => ({ default: m.Dashboard })),
);
const DocumentDetail = lazy(() =>
  import("./routes/Documents").then((m) => ({ default: m.DocumentDetail })),
);
const Documents = lazy(() =>
  import("./routes/Documents").then((m) => ({ default: m.Documents })),
);
const DocusealHealth = lazy(() =>
  import("./routes/DocusealHealth").then((m) => ({
    default: m.DocusealHealth,
  })),
);
const EinCorrections = lazy(() =>
  import("./routes/EinCorrections").then((m) => ({
    default: m.EinCorrections,
  })),
);
const EinCorrectionDetail = lazy(() =>
  import("./routes/EinCorrections").then((m) => ({
    default: m.EinCorrectionDetail,
  })),
);
const Fax = lazy(() =>
  import("./routes/Fax").then((m) => ({ default: m.Fax })),
);
const FaxDetail = lazy(() =>
  import("./routes/Fax").then((m) => ({ default: m.FaxDetail })),
);
const MailList = lazy(() =>
  import("./routes/Mail").then((m) => ({ default: m.MailList })),
);
const MailDetailPage = lazy(() =>
  import("./routes/Mail").then((m) => ({ default: m.MailDetailPage })),
);
const FederalTax = lazy(() =>
  import("./routes/FederalTax").then((m) => ({ default: m.FederalTax })),
);
const FederalTaxDetail = lazy(() =>
  import("./routes/FederalTax").then((m) => ({ default: m.FederalTaxDetail })),
);
const Form7004List = lazy(() =>
  import("./routes/form-7004/Form7004List").then((m) => ({
    default: m.Form7004List,
  })),
);
const Form7004DetailPage = lazy(() =>
  import("./routes/form-7004/Form7004Detail").then((m) => ({
    default: m.Form7004DetailPage,
  })),
);
const TaxYearWaitlist = lazy(() =>
  import("./routes/tax-year-waitlist/TaxYearWaitlist").then((m) => ({
    default: m.TaxYearWaitlist,
  })),
);
const Formation = lazy(() =>
  import("./routes/Formation").then((m) => ({ default: m.Formation })),
);
const FormationDetail = lazy(() =>
  import("./routes/formation/FormationDetail").then((m) => ({
    default: m.FormationDetail,
  })),
);
const MailItemDetail = lazy(() =>
  import("./routes/Mailroom").then((m) => ({ default: m.MailItemDetail })),
);
const Mailroom = lazy(() =>
  import("./routes/Mailroom").then((m) => ({ default: m.Mailroom })),
);
const NotificationTemplate = lazy(() =>
  import("./routes/notifications/NotificationTemplate").then((m) => ({
    default: m.NotificationTemplate,
  })),
);
const Notifications = lazy(() =>
  import("./routes/Notifications").then((m) => ({ default: m.Notifications })),
);
const RegisteredAgent = lazy(() =>
  import("./routes/registered-agent/RegisteredAgent").then((m) => ({
    default: m.RegisteredAgent,
  })),
);
const RegisteredAgentDetail = lazy(() =>
  import("./routes/registered-agent/RegisteredAgent").then((m) => ({
    default: m.RegisteredAgentDetail,
  })),
);
const Operations = lazy(() =>
  import("./routes/Operations").then((m) => ({ default: m.Operations })),
);
const OperationsIntegrity = lazy(() =>
  import("./routes/operations/integrity").then((m) => ({
    default: m.OperationsIntegrity,
  })),
);
const OperationsStorage = lazy(() =>
  import("./routes/operations/storage").then((m) => ({
    default: m.OperationsStorage,
  })),
);
const Trustpilot = lazy(() =>
  import("./routes/Trustpilot").then((m) => ({ default: m.Trustpilot })),
);
const OrderDetail = lazy(() =>
  import("./routes/OrderDetail").then((m) => ({ default: m.OrderDetail })),
);
const PaymentDetail = lazy(() =>
  import("./routes/payments/PaymentDetail").then((m) => ({
    default: m.PaymentDetail,
  })),
);
const Payments = lazy(() =>
  import("./routes/payments/Payments").then((m) => ({ default: m.Payments })),
);
const Orders = lazy(() =>
  import("./routes/Orders").then((m) => ({ default: m.Orders })),
);
const StateFilings = lazy(() =>
  import("./routes/StateFilings").then((m) => ({ default: m.StateFilings })),
);
const StateFilingDetail = lazy(() =>
  import("./routes/state-filings/StateFilingDetail").then((m) => ({
    default: m.StateFilingDetail,
  })),
);
const StateTaxRedirect = lazy(() =>
  import("./routes/state-filings/redirects").then((m) => ({
    default: m.StateTaxRedirect,
  })),
);
const StateTaxDetailRedirect = lazy(() =>
  import("./routes/state-filings/redirects").then((m) => ({
    default: m.StateTaxDetailRedirect,
  })),
);
const EmailCampaigns = lazy(() =>
  import("./routes/emails/campaigns/Campaigns").then((m) => ({
    default: m.EmailCampaigns,
  })),
);
const EmailTemplates = lazy(() =>
  import("./routes/emails/Templates").then((m) => ({
    default: m.EmailTemplates,
  })),
);
const EmailTemplateEditor = lazy(() =>
  import("./routes/emails/TemplateEditor").then((m) => ({
    default: m.EmailTemplateEditor,
  })),
);
const EmailCampaignCreate = lazy(() =>
  import("./routes/emails/campaigns/CampaignCreate").then((m) => ({
    default: m.EmailCampaignCreate,
  })),
);
const EmailCampaignDetail = lazy(() =>
  import("./routes/emails/campaigns/CampaignDetail").then((m) => ({
    default: m.EmailCampaignDetail,
  })),
);
const EmailContacts = lazy(() =>
  import("./routes/emails/Contacts").then((m) => ({
    default: m.EmailContacts,
  })),
);
const EmailSegments = lazy(() =>
  import("./routes/emails/Segments").then((m) => ({
    default: m.EmailSegments,
  })),
);
const EmailSegmentBuilder = lazy(() =>
  import("./routes/emails/SegmentBuilder").then((m) => ({
    default: m.EmailSegmentBuilder,
  })),
);
const EmailBrandPage = lazy(() =>
  import("./routes/emails/Brand").then((m) => ({
    default: m.EmailBrandPage,
  })),
);
const NotificationsRedirect = lazy(() =>
  import("./routes/emails/redirects").then((m) => ({
    default: m.NotificationsRedirect,
  })),
);
const NotificationTemplateRedirect = lazy(() =>
  import("./routes/emails/redirects").then((m) => ({
    default: m.NotificationTemplateRedirect,
  })),
);
const TrustpilotRedirect = lazy(() =>
  import("./routes/emails/redirects").then((m) => ({
    default: m.TrustpilotRedirect,
  })),
);
const AiSettings = lazy(() =>
  import("./routes/settings/AiSettings").then((m) => ({
    default: m.AiSettings,
  })),
);
const ThemeSettings = lazy(() =>
  import("./routes/settings/ThemeSettings").then((m) => ({
    default: m.ThemeSettings,
  })),
);
const Support = lazy(() =>
  import("./routes/support/Support").then((m) => ({ default: m.Support })),
);
const KnowledgeDetail = lazy(() =>
  import("./routes/support/KnowledgeDetail").then((m) => ({
    default: m.KnowledgeDetail,
  })),
);
const UserDetail = lazy(() =>
  import("./routes/UserDetail").then((m) => ({ default: m.UserDetail })),
);
const Users = lazy(() =>
  import("./routes/Users").then((m) => ({ default: m.Users })),
);
const WebhookDetail = lazy(() =>
  import("./routes/Webhooks").then((m) => ({ default: m.WebhookDetail })),
);
const Webhooks = lazy(() =>
  import("./routes/Webhooks").then((m) => ({ default: m.Webhooks })),
);
const WorkQueue = lazy(() =>
  import("./routes/WorkQueue").then((m) => ({ default: m.WorkQueue })),
);

/**
 * Pathless layout: Gate (and AdminShell) stay mounted across page changes.
 * A splat parent would remount the shell on every pathname, which looked like
 * a full reload.
 */
export const appRouteTree = (
  <>
    {/* Signed-out screens: the gate would answer them with the sign-in form. */}
    <Route path="forgot-password" element={<ForgotPassword />} />
    <Route path="reset-password" element={<ResetPassword />} />
    {/* The operator connector's OAuth pages, for the same reason: the
        visitor is signed out (or not an admin), so the gate cannot host them. */}
    <Route path="oauth/sign-in" element={<OAuthSignIn />} />
    <Route path="oauth/consent" element={<OAuthConsent />} />
    <Route element={<Gate />}>
      <Route index element={<WorkQueue />} />
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="companies" element={<Companies />} />
      <Route path="companies/:companyId" element={<CompanyDetail />} />
      <Route path="orders" element={<Orders />} />
      <Route path="orders/:id" element={<OrderDetail />} />
      <Route path="formation" element={<Formation />} />
      <Route path="formation/:id" element={<FormationDetail />} />
      <Route path="ein-corrections" element={<EinCorrections />} />
      <Route path="ein-corrections/:id" element={<EinCorrectionDetail />} />
      <Route path="registered-agent" element={<RegisteredAgent />} />
      <Route
        path="registered-agent/:serviceId"
        element={<RegisteredAgentDetail />}
      />
      <Route path="federal-tax" element={<FederalTax />} />
      {/* Static before the id: Form 7004 is not a federal-tax order id. */}
      <Route path="federal-tax/form-7004" element={<Form7004List />} />
      <Route
        path="federal-tax/form-7004/:orderId"
        element={<Form7004DetailPage />}
      />
      <Route
        path="federal-tax/tax-year-waitlist"
        element={<TaxYearWaitlist />}
      />
      <Route path="federal-tax/:orderId" element={<FederalTaxDetail />} />
      <Route path="state-filings" element={<StateFilings />} />
      <Route path="state-filings/:orderId" element={<StateFilingDetail />} />
      {/* The old State tax URLs, still in bookmarks, tickets and old emails. */}
      <Route path="state-tax" element={<StateTaxRedirect />} />
      <Route path="state-tax/:id" element={<StateTaxDetailRedirect />} />
      <Route path="documents" element={<Documents />} />
      <Route path="documents/:id" element={<DocumentDetail />} />
      <Route path="mailroom" element={<Mailroom />} />
      <Route path="mailroom/:id" element={<MailItemDetail />} />
      <Route path="fax" element={<Fax />} />
      <Route path="fax/:faxId" element={<FaxDetail />} />
      <Route path="mail" element={<MailList />} />
      <Route path="mail/:id" element={<MailDetailPage />} />
      <Route path="docuseal" element={<DocusealHealth />} />
      <Route path="billing" element={<Billing />} />
      <Route path="billing/invoices/:id" element={<InvoiceDetail />} />
      <Route
        path="billing/subscriptions/:id"
        element={<SubscriptionDetail />}
      />
      <Route
        path="billing/payment-intents/:id"
        element={<PaymentIntentDetail />}
      />
      <Route path="billing/disputes/:id" element={<DisputeDetail />} />
      <Route path="billing/payouts/:id" element={<PayoutDetail />} />
      <Route path="webhooks" element={<Webhooks />} />
      <Route path="webhooks/:id" element={<WebhookDetail />} />
      <Route path="approvals" element={<Approvals />} />
      <Route path="approvals/:id" element={<ApprovalDetailRoute />} />
      <Route path="accounting" element={<Accounting />} />
      <Route path="accounting/rules" element={<Accounting section="rules" />} />
      <Route
        path="accounting/connections"
        element={<Accounting section="connections" />}
      />
      <Route path="accounting/templates" element={<ChartTemplates />} />
      {/* One splat route for the whole book workspace: the tab is a path
          segment, and plans 15 and 16 add tabs inside BookWorkspace rather
          than here. Its own path opens Review or Overview (redirects.ts). */}
      <Route path="accounting/books/:bookId/*" element={<BookWorkspace />} />
      <Route path="alerts" element={<Alerts />} />
      {/* Emails (owner 2026-10-16): Campaigns lands on /emails; Transactional (the old
          Notifications) and Trustpilot live under it, and their old URLs redirect. */}
      <Route path="emails" element={<EmailCampaigns />} />
      <Route path="emails/campaigns/new" element={<EmailCampaignCreate />} />
      <Route
        path="emails/campaigns/:campaignId"
        element={<EmailCampaignDetail />}
      />
      <Route path="emails/templates" element={<EmailTemplates />} />
      <Route
        path="emails/templates/:templateId"
        element={<EmailTemplateEditor />}
      />
      <Route path="emails/contacts" element={<EmailContacts />} />
      <Route path="emails/segments" element={<EmailSegments />} />
      <Route
        path="emails/segments/:segmentId"
        element={<EmailSegmentBuilder />}
      />
      <Route path="emails/transactional" element={<Notifications />} />
      <Route
        path="emails/transactional/:registryId"
        element={<NotificationTemplate />}
      />
      <Route path="emails/trustpilot" element={<Trustpilot />} />
      <Route path="emails/brand" element={<EmailBrandPage />} />
      <Route path="notifications" element={<NotificationsRedirect />} />
      <Route
        path="notifications/:registryId"
        element={<NotificationTemplateRedirect />}
      />
      <Route path="operations" element={<Operations />} />
      <Route path="operations/integrity" element={<OperationsIntegrity />} />
      <Route path="operations/storage" element={<OperationsStorage />} />
      <Route path="trustpilot" element={<TrustpilotRedirect />} />
      <Route path="partners" element={<PartnersOverview />} />
      <Route path="partners/applications" element={<PartnerApplications />} />
      <Route
        path="partners/applications/:id"
        element={<PartnerApplicationDetail />}
      />
      <Route path="partners/list" element={<PartnersList />} />
      <Route path="partners/list/:partnerId" element={<PartnerDetail />} />
      <Route path="partners/earnings" element={<PartnerEarnings />} />
      <Route path="partners/payouts" element={<PartnerPayouts />} />
      <Route
        path="partners/payouts/:batchId"
        element={<PartnerPayoutBatch />}
      />
      <Route path="partners/settings" element={<PartnerFeesAndSettings />} />
      <Route path="payments" element={<Payments />} />
      <Route path="payments/:id" element={<PaymentDetail />} />
      <Route path="users" element={<Users />} />
      <Route path="users/:userId" element={<UserDetail />} />
      <Route path="support" element={<Support />} />
      <Route path="support/inbox" element={<Support />} />
      <Route path="support/inbox/:ticketId" element={<Support />} />
      <Route path="support/knowledge/:id" element={<KnowledgeDetail />} />
      <Route path="settings/ai" element={<AiSettings />} />
      <Route path="settings/theme" element={<ThemeSettings />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </>
);
